package org.example.project.yard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.example.project.engineservice.ServiceApi
import org.example.project.enginetabular.TABULAR_DOCUMENT_ICON_VALUE
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.screens_cus.CUSCeoDashboardStyles
import org.example.project.screens_sys.UnternehmenstammApi
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.SnapApi
import org.example.project.service.YardApi
import org.example.project.service.YardLiveSync
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.utils.All
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.css.minWidth
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.gridTemplateColumns
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val AUFTRAEGE_PANEL_WE_AKTUELL = "WE_AKTUELL"
private const val AUFTRAEGE_PANEL_WE_GLOBAL = "WE_GLOBAL"
private const val AUFTRAEGE_PANEL_WA_OFFEN = "WA_OFFEN"
private const val AUFTRAEGE_PANEL_WA_AKTUELL = "WA_AKTUELL"
private const val AUFTRAEGE_PANEL_WA_GLOBAL = "WA_GLOBAL"
private const val AUFTRAEGE_PANEL_GLOBAL = "GLOBAL"
private const val AUFTRAEGE_TYP_WE = "WE"
private const val AUFTRAEGE_TYP_WA = "WA"

@Composable
fun AuftraegeDashboardScreen() {
    AuftraegeDashboardContent(useSnapCompanies = false)
}

@Composable
fun SnapAuftragsDashboardScreen() {
    AuftraegeDashboardContent(useSnapCompanies = true)
}

@Composable
private fun AuftraegeDashboardContent(useSnapCompanies: Boolean) {
    Style(CUSCeoDashboardStyles)
    val scope = rememberCoroutineScope()

    var loading by remember { mutableStateOf(true) }
    var selectedPanel by remember { mutableStateOf(AUFTRAEGE_PANEL_GLOBAL) }
    var selectedFirmenId by remember { mutableStateOf("") }
    var sharedCompanies by remember { mutableStateOf<List<All>>(emptyList()) }
    var viewerCompany by remember { mutableStateOf<All?>(null) }
    var weRows by remember { mutableStateOf<List<All>>(emptyList()) }
    var waRows by remember { mutableStateOf<List<All>>(emptyList()) }
    var documentsByKey by remember { mutableStateOf<Map<String, List<All>>>(emptyMap()) }
    var lieferscheinUploadEntry by remember { mutableStateOf<All?>(null) }
    var stableOpenWaRows by remember { mutableStateOf<List<All>>(emptyList()) }
    var searchedRows by remember { mutableStateOf<List<All>>(emptyList()) }
    var searchedDocumentsByKey by remember { mutableStateOf<Map<String, List<All>>>(emptyMap()) }
    val monthKey = remember { auftraegeDashboardCurrentMonthKey() }
    val currentUserId = LoginService.getId().trim()
    val currentFirmenId = LoginService.getUser()?.firmenId.orEmpty().trim()
        .ifBlank { GlobalManager.currentFirmenId.trim() }
    val dashboardScreenKey = if (useSnapCompanies) "SnapAuftragsDashboard" else "AuftraegeDashboardScreen"
    val tableScreenPrefix = if (useSnapCompanies) "SnapAuftragsDashboard" else "AuftraegeDashboard"
    val snapLiveVersion = if (useSnapCompanies) GlobalManager.jsonDomainVersion("snap") else 0L
    val yardLiveVersion = maxOf(YardLiveSync.version(includeSnap = useSnapCompanies), snapLiveVersion)

    fun searchDashboard(filters: Map<String, String>) {
        scope.launch {
            val filtered = YardApi.searchAuftraegeDashboard(filters, selectedFirmenId, selectedPanel, monthKey, useSnapCompanies)
            searchedRows = filtered
            searchedDocumentsByKey = auftraegeDashboardLoadDocuments(filtered)
        }
    }

    LaunchedEffect(currentUserId, currentFirmenId, snapLiveVersion) {
        loading = true
        viewerCompany = if (useSnapCompanies) runCatching { UnternehmenstammApi.loadUnternehmen(currentFirmenId) }.getOrNull() else null
        sharedCompanies = if (useSnapCompanies) {
            runCatching { SnapApi.loadAuftragCompanies(currentUserId, currentFirmenId) }.getOrDefault(emptyList())
        } else {
            emptyList()
        }
        selectedFirmenId = if (useSnapCompanies) {
            sharedCompanies.firstOrNull { it.firmenId == selectedFirmenId }?.firmenId
                ?: sharedCompanies.firstOrNull()?.firmenId.orEmpty()
        } else {
            currentFirmenId
        }
        if (selectedFirmenId.isBlank()) {
            weRows = emptyList()
            waRows = emptyList()
            documentsByKey = emptyMap()
            loading = false
        }
    }

    LaunchedEffect(selectedFirmenId, currentFirmenId, viewerCompany, yardLiveVersion) {
        if (selectedFirmenId.isBlank()) return@LaunchedEffect
        loading = true
        val loadedWeRaw = runCatching { YardApi.loadWeLkwListe(selectedFirmenId) }.getOrDefault(emptyList())
        val loadedWaRaw = runCatching { YardApi.loadWaLkwListe(selectedFirmenId) }.getOrDefault(emptyList())
        val loadedWe = (if (useSnapCompanies) loadedWeRaw.filter { auftraegeDashboardRecordHasViewerFirma(it, currentFirmenId, viewerCompany) } else loadedWeRaw)
            .filter { it.zustand >= ZustandManager.ABGESCHLOSSEN }
        val loadedWa = (if (useSnapCompanies) loadedWaRaw.filter { auftraegeDashboardRecordHasViewerFirma(it, currentFirmenId, viewerCompany) } else loadedWaRaw)
            .filter { it.zustand >= ZustandManager.ABGESCHLOSSEN }
        val nextWeRows = loadedWe.map { it.copy(art = AUFTRAEGE_TYP_WE) }
        val nextWaRows = loadedWa.map { it.copy(art = AUFTRAEGE_TYP_WA) }
        weRows = nextWeRows
        waRows = nextWaRows
        documentsByKey = auftraegeDashboardLoadDocuments(nextWeRows + nextWaRows)
        loading = false
    }

    val documentedWeRows = weRows.filter { auftraegeDashboardIsDocumented(it, documentsByKey) }
    val documentedWaRows = waRows.filter { auftraegeDashboardIsDocumented(it, documentsByKey) }
    val documentedRows = documentedWeRows + documentedWaRows
    val currentRows = documentedRows.filter { auftraegeDashboardMonthKey(it) == monthKey }
    val currentWeRows = currentRows.filter { auftraegeDashboardTyp(it) == AUFTRAEGE_TYP_WE }
    val currentWaRows = currentRows.filter { auftraegeDashboardTyp(it) == AUFTRAEGE_TYP_WA }
    val openWaRows = if (useSnapCompanies) {
        emptyList()
    } else {
        documentedWaRows.filter { auftraegeDashboardDocument(it, documentsByKey, "lieferschein") == null }
    }
    LaunchedEffect(openWaRows, loading, useSnapCompanies) {
        if (!useSnapCompanies && !loading) stableOpenWaRows = openWaRows
    }
    val visibleOpenWaRows = if (!useSnapCompanies && loading && stableOpenWaRows.isNotEmpty()) stableOpenWaRows else openWaRows
    val tiles = listOfNotNull(
        auftraegeDashboardTile(AUFTRAEGE_PANEL_WE_AKTUELL, "Wareneingang Monat", "images/we.svg", currentWeRows.size.toString()),
        auftraegeDashboardTile(AUFTRAEGE_PANEL_WE_GLOBAL, "Wareneingang Global", "images/we.svg", documentedWeRows.size.toString()),
        if (!useSnapCompanies) auftraegeDashboardTile(AUFTRAEGE_PANEL_WA_OFFEN, "Warenausgang offen", "images/wa.svg", visibleOpenWaRows.size.toString()) else null,
        auftraegeDashboardTile(AUFTRAEGE_PANEL_WA_AKTUELL, "Warenausgang Monat", "images/wa.svg", currentWaRows.size.toString()),
        auftraegeDashboardTile(AUFTRAEGE_PANEL_WA_GLOBAL, "Warenausgang Global", "images/wa.svg", documentedWaRows.size.toString()),
        auftraegeDashboardTile(AUFTRAEGE_PANEL_GLOBAL, "Vorgänge Global", "images/order.svg", documentedRows.size.toString())
    )

    Div({ classes(CUSCeoDashboardStyles.screenRoot) }) {
        Div({ classes(CUSCeoDashboardStyles.stickyTop) }) {
            if (useSnapCompanies) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        property("justify-content", "center")
                        property("margin-bottom", "12px")
                    }
                }) {
                    Div({
                        style {
                            minWidth(260.px)
                            property("width", "auto")
                            property("max-width", "420px")
                        }
                    }) {
                        AppDropdown(
                            options = sharedCompanies
                                .filter { it.firmenId.isNotBlank() }
                                .map {
                                    All(
                                        key = it.firmenId,
                                        label = it.label.ifBlank { it.firma.ifBlank { it.kunde.ifBlank { it.firmenId } } }
                                    )
                                },
                            selectedValue = selectedFirmenId,
                            onChange = { selectedFirmenId = it },
                            placeholder = "Firma",
                            appearance = AppDropdownAppearance.Underline,
                            menuMode = AppDropdownMenuMode.Overlay
                        )
                    }
                }
            }
            Div({ classes(CUSCeoDashboardStyles.tileScrollHost) }) {
                Div({
                    classes(CUSCeoDashboardStyles.grid)
                    style {
                        gridTemplateColumns("repeat(${tiles.size}, 240px)")
                        property("width", "max-content")
                        property("margin-left", "auto")
                        property("margin-right", "auto")
                    }
                }) {
                    tiles.forEach { tile ->
                        AuftraegeDashboardTile(
                            tile = tile,
                            loading = loading,
                            active = selectedPanel == tile.key,
                            onClick = { selectedPanel = tile.key }
                        )
                    }
                }
            }
        }

        Div({ classes(CUSCeoDashboardStyles.tabularArea) }) {
            when (selectedPanel) {
                AUFTRAEGE_PANEL_WE_AKTUELL -> AuftraegeDashboardVorgaengeTable("Wareneingang Monat", searchedRows, searchedDocumentsByKey, includeTyp = false, includeLieferschein = false, includeWeKontrolle = true, rollenKey = dashboardScreenKey, screenKeyPrefix = tableScreenPrefix, onSearch = ::searchDashboard)
                AUFTRAEGE_PANEL_WE_GLOBAL -> AuftraegeDashboardVorgaengeTable("Wareneingang Global", searchedRows, searchedDocumentsByKey, includeTyp = false, includeLieferschein = false, includeWeKontrolle = true, rollenKey = dashboardScreenKey, screenKeyPrefix = tableScreenPrefix, onSearch = ::searchDashboard)
                AUFTRAEGE_PANEL_WA_OFFEN -> AuftraegeDashboardVorgaengeTable("Warenausgang offen", searchedRows, searchedDocumentsByKey, includeTyp = false, includeLieferschein = true, rollenKey = dashboardScreenKey, screenKeyPrefix = tableScreenPrefix, warningCount = searchedRows.size, onUploadLieferschein = { lieferscheinUploadEntry = it }, onSearch = ::searchDashboard)
                AUFTRAEGE_PANEL_WA_AKTUELL -> AuftraegeDashboardVorgaengeTable("Warenausgang Monat", searchedRows, searchedDocumentsByKey, includeTyp = false, includeLieferschein = true, rollenKey = dashboardScreenKey, screenKeyPrefix = tableScreenPrefix, onSearch = ::searchDashboard)
                AUFTRAEGE_PANEL_WA_GLOBAL -> AuftraegeDashboardVorgaengeTable("Warenausgang Global", searchedRows, searchedDocumentsByKey, includeTyp = false, includeLieferschein = true, rollenKey = dashboardScreenKey, screenKeyPrefix = tableScreenPrefix, onSearch = ::searchDashboard)
                else -> AuftraegeDashboardVorgaengeTable("Vorgänge Global", searchedRows, searchedDocumentsByKey, includeTyp = true, includeLieferschein = true, rollenKey = dashboardScreenKey, screenKeyPrefix = tableScreenPrefix, onSearch = ::searchDashboard)
            }
        }
        lieferscheinUploadEntry?.let { uploadEntry ->
            YardLieferscheinUploadPopup(
                entry = uploadEntry,
                onClose = { lieferscheinUploadEntry = null },
                onUploaded = {
                    val key = auftraegeDashboardDocumentListKey(uploadEntry)
                    val docs = ServiceApi.serviceDocuments(auftraegeDashboardServiceType(uploadEntry), auftraegeDashboardServiceEntryId(uploadEntry))
                    val nextDocuments = documentsByKey + (key to docs)
                    documentsByKey = nextDocuments
                    val found = auftraegeDashboardDocument(uploadEntry, nextDocuments, "lieferschein") != null
                    if (found) lieferscheinUploadEntry = null
                    found
                }
            )
        }
    }
}

@Composable
private fun AuftraegeDashboardTile(tile: All, loading: Boolean, active: Boolean, onClick: () -> Unit) {
    Div({
        if (active) {
            classes(CUSCeoDashboardStyles.tile, CUSCeoDashboardStyles.tileClickable, CUSCeoDashboardStyles.tileActive)
        } else {
            classes(CUSCeoDashboardStyles.tile, CUSCeoDashboardStyles.tileClickable)
        }
        onClick { onClick() }
    }) {
        Div({ classes(CUSCeoDashboardStyles.iconSlot) }) {
            Img(src = tile.icon, attrs = { classes(CUSCeoDashboardStyles.icon) })
        }
        Div({ classes(CUSCeoDashboardStyles.textColumn) }) {
            Div({ classes(CUSCeoDashboardStyles.tileLabel) }) { Text(tile.label) }
            Div({
                classes(CUSCeoDashboardStyles.value)
                if (tile.key == AUFTRAEGE_PANEL_WA_OFFEN) style { color(Color("#dc2626")) }
            }) {
                Text(if (loading) "..." else tile.bezeichnung)
            }
        }
    }
}

@Composable
private fun AuftraegeDashboardVorgaengeTable(
    title: String,
    rows: List<All>,
    documentsByKey: Map<String, List<All>>,
    includeTyp: Boolean,
    includeLieferschein: Boolean,
    includeWeKontrolle: Boolean = false,
    rollenKey: String,
    screenKeyPrefix: String,
    warningCount: Int = 0,
    onUploadLieferschein: ((All) -> Unit)? = null,
    onSearch: (Map<String, String>) -> Unit
) {
    val scope = rememberCoroutineScope()
    var selectedRowId by remember(title, rows) { mutableStateOf("") }
    val selectedUploadEntry = rows.firstOrNull { auftraegeDashboardRowKey(it) == selectedRowId }
    val documentKinds = listOf("palettenschein", "prozess", "qualitaet") +
        (if (includeLieferschein) listOf("lieferschein") else emptyList()) +
        (if (includeWeKontrolle) listOf("web") else emptyList())
    val documentHeaders = listOf("Palettenschein", "Prozessdokumentation", "Qualitätsdokumentation") +
        (if (includeLieferschein) listOf("Lieferschein") else emptyList()) +
        (if (includeWeKontrolle) listOf("WE Fehlerprotokoll") else emptyList())
    val isWePanel = title.startsWith("Wareneingang")
    val isWaPanel = title.startsWith("Warenausgang")
    val hasWeColumn = !isWaPanel
    val hasWaColumn = !isWePanel
    val palettenHeaders =
        (if (hasWeColumn) listOf("Paletten Eingang") else emptyList()) +
        (if (hasWaColumn) listOf("Paletten Ausgang") else emptyList())
    val headers = (if (includeTyp) listOf("Typ") else emptyList()) +
        listOf("Datum", "LS") + palettenHeaders +
        listOf("Lieferant", "Unternehmer", "Auftraggeber", "Empfänger", "Fahrer", "LKW", "Auflieger", "Zustand") +
        documentHeaders
    val docOffset = headers.size - documentHeaders.size
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            width(100.percent)
        }
    }) {
        if (warningCount > 0) AuftraegeDashboardWarningBanner(warningCount)
        AuftraegeDashboardTabularScreen(
        screenKey = "${screenKeyPrefix}_${title.filter { it.isLetterOrDigit() }}",
        rollenKey = rollenKey,
        title = title,
        headers = headers,
        data = rows.sortedWith(compareByDescending<All> { auftraegeDashboardSortableDate(it.datum) }.thenByDescending { it.lfdNr }).map { row ->
            auftraegeDashboardRowKey(row) to (
                (if (includeTyp) listOf(auftraegeDashboardTypLabel(row)) else emptyList()) +
                    listOf(
                        row.datum,
                        row.lieferscheinnummer
                    ) +
                    (if (hasWeColumn) listOf(
                        if (auftraegeDashboardTyp(row) == AUFTRAEGE_TYP_WE) auftraegeDashboardPalettenEingang(row).toString() else ""
                    ) else emptyList()) +
                    (if (hasWaColumn) listOf(
                        if (auftraegeDashboardTyp(row) == AUFTRAEGE_TYP_WA) auftraegeDashboardPalettenAusgang(row).toString() else ""
                    ) else emptyList()) +
                    listOf(
                        auftraegeDashboardCompanyName(row.lieferant),
                        auftraegeDashboardCompanyName(row.unternehmer),
                        auftraegeDashboardCompanyName(row.auftraggeber),
                        auftraegeDashboardCompanyName(row.empfaenger),
                        row.fahrername,
                        row.lkwkennzeichen,
                        row.aufliegerkennzeichen,
                        "${row.zustand} - ${auftraegeDashboardZustandText(row)}",
                    ) + documentKinds.map { kind -> auftraegeDashboardDocumentIcon(row, documentsByKey, kind) }
                )
        },
        extraNavButtons = if (onUploadLieferschein != null) listOf(
            TabularToolbarButton(
                label = "Lieferscheine hinzufügen",
                icon = "images/upload.svg",
                enabled = selectedUploadEntry != null,
                onClick = { selectedUploadEntry?.let(onUploadLieferschein) }
            )
        ) else emptyList(),
        onRowClick = { selectedRowId = it },
        onCellClick = { rowId, colIndex ->
            if (colIndex >= docOffset) {
                val kind = documentKinds.getOrNull(colIndex - docOffset).orEmpty()
                scope.launch { auftraegeDashboardOpenDocument(rows, documentsByKey, rowId, kind) }
            }
        },
        onSearch = onSearch
    )
    }
}

@Composable
private fun AuftraegeDashboardWarningBanner(count: Int) {
    Div({
        style {
            width(100.percent)
            boxSizing("border-box")
            padding(12.px, 18.px)
            borderRadius(8.px)
            property("border", "1px solid rgba(239,68,68,0.72)")
            backgroundColor(Color("transparent"))
            color(org.example.project.dashboard.DashboardTheme.TEXT_LIGHT)
            fontWeight("700")
            textAlign("center")
        }
    }) {
        Text("Achtung! ")
        Span({ style { color(Color("#dc2626")) } }) { Text(count.toString()) }
        Text(" Aufträge benötigen Lieferscheine!")
    }
}

@Composable
private fun AuftraegeDashboardTabularScreen(
    screenKey: String,
    rollenKey: String,
    title: String,
    headers: List<String>,
    data: List<Pair<String, List<String>>>,
    extraNavButtons: List<TabularToolbarButton> = emptyList(),
    onRowClick: ((String) -> Unit)? = null,
    onCellClick: ((String, Int) -> Unit)? = null,
    onSearch: (Map<String, String>) -> Unit
) {
    Div({ classes(CUSCeoDashboardStyles.verbrauchTable) }) {
        TabularScreen(
            screenKey = screenKey,
            rollenKey = rollenKey,
            title = title,
            headers = headers,
            data = data,
            editable = false,
            hardReadOnly = true,
            includeMetadata = false,
            nonEditableCols = headers.toSet(),
            allowCreate = false,
            allowDelete = false,
            allowSave = false,
            allowImportButton = false,
            allowExportButton = true,
            allowRefreshButton = true,
            allowSearchButton = true,
            onSearchWithFilters = onSearch,
            allowSettingsButton = true,
            extraNavButtons = extraNavButtons,
            useUserSettings = false,
            smartMode = true,
            onRowClick = onRowClick,
            onCellClick = onCellClick,
            onCommit = {}
        )
    }
}

private suspend fun auftraegeDashboardLoadDocuments(rows: List<All>): Map<String, List<All>> {
    val result = mutableMapOf<String, List<All>>()
    rows
        .filter { it.lieferscheinnummer.isNotBlank() }
        .map { auftraegeDashboardDocumentListKey(it) to it }
        .distinctBy { it.first }
        .forEach { (key, row) ->
            val type = auftraegeDashboardServiceType(row)
            val entryId = auftraegeDashboardServiceEntryId(row)
            result[key] = runCatching { ServiceApi.serviceDocuments(type, entryId) }.getOrDefault(emptyList())
        }
    return result
}

private fun auftraegeDashboardTile(key: String, label: String, icon: String, value: String): All =
    All(key = key, label = label, icon = icon, bezeichnung = value)

private fun auftraegeDashboardDocumentIcon(row: All, documentsByKey: Map<String, List<All>>, kind: String): String =
    if (auftraegeDashboardDocument(row, documentsByKey, kind) != null) TABULAR_DOCUMENT_ICON_VALUE else ""

private fun auftraegeDashboardIsDocumented(row: All, documentsByKey: Map<String, List<All>>): Boolean {
    val required = listOf("palettenschein", "prozess", "qualitaet")
    return required.all { auftraegeDashboardDocument(row, documentsByKey, it) != null }
}

private fun auftraegeDashboardDocument(row: All, documentsByKey: Map<String, List<All>>, kind: String): All? {
    val expected = auftraegeDashboardExpectedFileName(row, kind).lowercase()
    val expectedLoose = expected.substringBeforeLast(".")
    return documentsByKey[auftraegeDashboardDocumentListKey(row)]
        .orEmpty()
        .firstOrNull { doc ->
            val file = doc.file.ifBlank { "${doc.name}.pdf" }.lowercase()
            file == expected || file.substringBeforeLast(".") == expectedLoose
        }
        ?: documentsByKey[auftraegeDashboardDocumentListKey(row)]
            .orEmpty()
            .firstOrNull { doc ->
                val file = doc.file.ifBlank { "${doc.name}.pdf" }.lowercase()
                val prefix = auftraegeDashboardDocumentPrefix(row).lowercase()
                file.startsWith(prefix) && when (kind) {
                    "palettenschein" -> file.contains("palettenschein")
                    "prozess" -> file.contains("prozessdokumentation")
                    "qualitaet" -> file.contains("qualitaetsdokumentation") || file.contains("qualitätsdokumentation")
                    "lieferschein" -> file.contains("lieferschein")
                    "web" -> file.contains("we_fehlerprotokoll") || file.contains("fehlerprotokoll") || file.contains("wareneingangsbericht")
                    else -> false
                }
            }
}

private suspend fun auftraegeDashboardOpenDocument(rows: List<All>, documentsByKey: Map<String, List<All>>, rowId: String, kind: String) {
    val row = rows.firstOrNull { auftraegeDashboardRowKey(it) == rowId } ?: return
    val doc = auftraegeDashboardDocument(row, documentsByKey, kind) ?: return
    ServiceApi.openServiceDocumentMeta(auftraegeDashboardServiceType(row), auftraegeDashboardServiceEntryId(row), doc)
}

private fun auftraegeDashboardExpectedFileName(row: All, kind: String): String {
    val ls = row.lieferscheinnummer.ifBlank { "auftrag" }
    val lkw = row.lfdNr.takeIf { it > 0 }?.toString() ?: row.id.ifBlank { "1" }
    val standort = auftraegeDashboardStandortKey(row).takeIf { it.isNotBlank() }?.let { "-standort-$it" }.orEmpty()
    return when (kind) {
        "palettenschein" -> "${ls}-${lkw}${standort}_palettenschein.pdf"
        "prozess" -> "${ls}-${lkw}${standort}_prozessdokumentation.pdf"
        "qualitaet" -> "${ls}_${lkw}${standort.replace("-", "_")}_qualitaetsdokumentation.pdf"
        "lieferschein" -> "${ls}-${lkw}_lieferschein.pdf"
        "web" -> "${ls}_${lkw}${standort.replace("-", "_")}_we_fehlerprotokoll.pdf"
        else -> ""
    }
}

private fun auftraegeDashboardDocumentPrefix(row: All): String {
    val ls = row.lieferscheinnummer.ifBlank { "auftrag" }
    val lkw = row.lfdNr.takeIf { it > 0 }?.toString() ?: row.id.ifBlank { "1" }
    return "$ls-$lkw"
}

private fun auftraegeDashboardStandortKey(row: All): String =
    row.waStandortId
        .ifBlank { row.slotStandortId }
        .ifBlank { row.platzcode }
        .ifBlank { row.standort }
        .trim()
        .filter { it.isLetterOrDigit() || it == '-' || it == '_' }

private fun auftraegeDashboardDocumentListKey(row: All): String =
    "${auftraegeDashboardServiceType(row)}:${auftraegeDashboardServiceEntryId(row)}"

private fun auftraegeDashboardServiceEntryId(row: All): String {
    val ls = row.lieferscheinnummer.trim()
    if (ls.isBlank()) return ""
    val id = row.id.trim()
    val lfdNr = row.lfdNr.takeIf { it > 0 }?.toString().orEmpty()
    return if (id.isNotBlank() || lfdNr.isNotBlank()) "$ls|$id|$lfdNr" else ls
}

private fun auftraegeDashboardServiceType(row: All): String =
    if (auftraegeDashboardTyp(row) == AUFTRAEGE_TYP_WA) "yard-wa" else "yard-we"

private fun auftraegeDashboardRowKey(row: All): String =
    listOf(auftraegeDashboardTyp(row), row.lieferscheinnummer, row.lfdNr.toString(), row.id, auftraegeDashboardStandortKey(row))
        .joinToString(":")

private fun auftraegeDashboardTyp(row: All): String =
    row.art.ifBlank { row.auftragsart }.uppercase().let { if (it == AUFTRAEGE_TYP_WA) AUFTRAEGE_TYP_WA else AUFTRAEGE_TYP_WE }

private fun auftraegeDashboardTypLabel(row: All): String =
    if (auftraegeDashboardTyp(row) == AUFTRAEGE_TYP_WA) "WA" else "WE"

private fun auftraegeDashboardPalettenEingang(row: All): Int =
    row.yardabschlussIstPaletten.takeIf { it > 0 }
        ?: (row.mobilWePalettenUnbeschaedigt + row.mobilWePalettenBruch).takeIf { it > 0 }
        ?: row.palettenanzahl.takeIf { row.zustand >= ZustandManager.ABGESCHLOSSEN && it > 0 }
        ?: 0

private fun auftraegeDashboardPalettenAusgang(row: All): Int =
    row.yardabschlussIstPaletten.takeIf { it > 0 }
        ?: row.mobilIstPaletten.takeIf { it > 0 }
        ?: row.palettenanzahl.takeIf { row.zustand >= ZustandManager.ABGESCHLOSSEN && it > 0 }
        ?: 0

private fun auftraegeDashboardCompanyName(value: String): String {
    val trimmed = value.trim()
    return trimmed
        .split("\n", "\r", "|", ";", ",")
        .firstOrNull()
        .orEmpty()
        .trim()
        .ifBlank { trimmed }
}

private fun auftraegeDashboardRecordHasViewerFirma(entry: All, viewerFirmenId: String, viewerCompany: All?): Boolean {
    val tokens = auftraegeDashboardViewerTokens(viewerFirmenId, viewerCompany)
    if (tokens.isEmpty()) return false
    return auftraegeDashboardRoleFields(entry).any { field ->
        tokens.any { token -> field.contains(token, ignoreCase = true) }
    }
}

private fun auftraegeDashboardRoleFields(entry: All): List<String> =
    listOf(
        entry.lieferant,
        entry.unternehmer,
        entry.auftraggeber,
        entry.empfaenger,
        entry.lieferantFirmenId,
        entry.unternehmerFirmenId,
        entry.auftraggeberFirmenId,
        entry.empfaengerFirmenId
    )
        .map { it.trim() }
        .filter { it.isNotBlank() }

private fun auftraegeDashboardViewerTokens(viewerFirmenId: String, viewerCompany: All?): List<String> {
    val address = viewerCompany?.let {
        listOf(it.strasse, it.hausnummer, it.plz, it.ort)
            .map { value -> value.trim() }
            .filter { value -> value.isNotBlank() }
            .joinToString(" ")
    }.orEmpty()
    return listOf(
        viewerFirmenId,
        viewerCompany?.firmenId.orEmpty(),
        viewerCompany?.lsId.orEmpty(),
        viewerCompany?.key.orEmpty(),
        viewerCompany?.firma.orEmpty(),
        viewerCompany?.kunde.orEmpty(),
        viewerCompany?.label.orEmpty(),
        address
    )
        .map { it.trim() }
        .filter { it.length >= 3 }
        .distinctBy { it.lowercase() }
}

private fun auftraegeDashboardZustandText(row: All): String =
    when (row.zustand) {
        ZustandManager.IN_VERLADUNG -> if (auftraegeDashboardTyp(row) == AUFTRAEGE_TYP_WA) "In Verladung" else "In Entladung"
        ZustandManager.TEILENTLADEN -> "Teilentladen"
        ZustandManager.TEILVERLADEN -> "Teilverladen"
        else -> ZustandManager.textFor(row.zustand)
    }

private fun auftraegeDashboardMonthKey(row: All): String =
    auftraegeDashboardMonthKeyFromText(row.uhrzeitAbgeschlossen)
        .ifBlank { auftraegeDashboardMonthKeyFromText(row.datum) }
        .ifBlank { auftraegeDashboardMonthKeyFromText(row.erstelltAm) }

private fun auftraegeDashboardMonthKeyFromText(value: String): String {
    val text = value.trim()
    if (text.length >= 7 && text[4] == '_') return text.take(7)
    if (text.length >= 7 && text[4] == '-') return text.take(7).replace("-", "_")
    val parts = text.split(".", "/", "-")
    return when {
        parts.size >= 3 && parts[2].length == 4 -> "${parts[2]}_${parts[1].padStart(2, '0')}"
        parts.size >= 2 && parts[0].length == 4 -> "${parts[0]}_${parts[1].padStart(2, '0')}"
        else -> ""
    }
}

private fun auftraegeDashboardSortableDate(value: String): String {
    val parts = value.trim().split(".", "/", "-")
    return when {
        parts.size >= 3 && parts[2].length == 4 -> "${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}"
        parts.size >= 3 && parts[0].length == 4 -> "${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}"
        else -> value
    }
}

private fun auftraegeDashboardCurrentMonthKey(): String {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    return "${now.year}_${now.monthNumber.toString().padStart(2, '0')}"
}
